package one.devsbox.www.ui.apps.wheel.detail

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.DeleteOutline
import compose.assets.svg.EditOutline
import kotlinx.coroutines.launch
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.ArgsIdWithParent
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.getString
import tk.mallumo.compose.navigation.navTo_AppWheelEUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

@VM
class AppWheelDVM : NavigationViewModel() {
    private lateinit var ctx: AppWheelDCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    data class Data(
        val appInstance: SharedIO.AppInstance,
        val wheelApp: one.devsbox.shared.a.App.Wheel
    )

    var state by mutableStateOf<DataState<Data>>(DataState.Loading())
        private set

    var showDeleteConfirmation by mutableStateOf(false)
    var showDeleteError by mutableStateOf(false)

    interface Action {
        fun edit(id: Long)
        fun delete()
        fun confirmDelete()
        fun dismissDeleteConfirmation()
        fun dismissDeleteError()
        fun back()
        fun reload()
    }

    val action = object : Action {
        override fun back() {
            ctx.nav.up()
        }

        override fun reload() {
            loadApp(ctx.args.id)
        }

        override fun edit(id: Long) {
            state.entry?.appInstance?.app?.id?.also {
                ctx.nav.navTo_AppWheelEUI(
                    ArgsIdWithParent(
                        id,
                        it
                    )
                )
            }

        }

        override fun delete() {
            showDeleteConfirmation = true
        }

        override fun dismissDeleteConfirmation() {
            showDeleteConfirmation = false
        }

        override fun dismissDeleteError() {
            showDeleteError = false
        }

        override fun confirmDelete() {
            showDeleteConfirmation = false
            state = DataState.Loading(state.entry)
            scope.launch {
                runCatching {
                    Repository.ads.app.instance.delete(ctx.args.id)
                }.onFailure {
                    it.printStackTrace()
                    showDeleteError = true
                    state = DataState.Result(state.entry!!)
                }.onSuccess {
                    ctx.nav.up()
                }
            }
        }
    }

    fun setup(ctx: AppWheelDCtx) {
        this.ctx = ctx
        loadApp(ctx.args.id)
    }

    private fun loadApp(id: Long) {
        state = DataState.Loading()
        scope.launch {
            try {
                val appInstance = Repository.ads.app.instance.get(id)
                val wheelApp = appInstance.data as one.devsbox.shared.a.App.Wheel
                val data = Data(appInstance, wheelApp)
                state = DataState.Result(data)
            } catch (it: Throwable) {
                val fallbackError = try {
                    getString(Res.string.ui_error_unknown)
                } catch (e: Throwable) {
                    "Unknown error"
                }
                state = DataState.Error(it.message ?: fallbackError)
            }
            setupActions(state)
        }
    }

    private fun setupActions(result: DataState<Data>) {
        contentActions = result.entry?.run {
            buildList {
                if (ctx.me.permission.application.write) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_locations_detail_btn_edit,
                            color = ctx.colorSecondaryContainer,
                            icon = Svg.EditOutline
                        ) {
                            action.edit(ctx.args.id)
                        }
                    )
                }
                if (ctx.me.permission.application.delete) {
                    add(
                        ScreenContentAction(
                            text = Res.string.ui_locations_detail_btn_delete,
                            color = ctx.colorErrorContainer,
                            icon = Svg.DeleteOutline
                        ) {
                            action.delete()
                        }
                    )
                }
            }
        } ?: emptyList()
    }

    override fun onRelease() {}
}
